/*
 This file is part of GNU Taler
 (C) 2018-2019 GNUnet e.V.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import { canonicalizeBaseUrl, j2s } from "./helpers.js";
import { Logger } from "./logging.js";

/**
 * Type-safe codecs for converting from/to JSON.
 */

const logger = new Logger("codec.ts");

/**
 * Error thrown when decoding fails.
 */
export class DecodingError extends Error {
  constructor(message: string) {
    super(message);
    Object.setPrototypeOf(this, DecodingError.prototype);
    this.name = "DecodingError";
  }
}

/**
 * Context information to show nicer error messages when decoding fails.
 */
export interface Context {
  readonly path?: string[];
}

export function renderContext(c?: Context): string {
  const p = c?.path;
  if (p) {
    return p.join(".");
  } else {
    return "(unknown)";
  }
}

function joinContext(c: Context | undefined, part: string): Context {
  const path = c?.path ?? [];
  return {
    path: path.concat([part]),
  };
}

/**
 * A codec converts untyped JSON to a typed object.
 */
export interface Codec<V> {
  /**
   * Decode untyped JSON to an object of type [[V]].
   */
  readonly decode: (x: any, c?: Context) => V;

  /**
   * Phantom member, never present at runtime.
   *
   * Without it V occurs only in an output position and Codec<V> is
   * covariant, so a required codec would satisfy an optional field and
   * reject responses that legitimately omit it.  Naming V in an input
   * position too makes Codec invariant.
   */
  readonly _invariant?: (x: V) => void;
}

/**
 * A codec built from an ObjectCodecBuilder object.
 */
export interface ObjectCodec<V> extends Codec<V> {
  /**
   * Get properties from builder. This method is only supported for codecs
   * built from ObjectCodecBuilder.
   */
  readonly getProps: () => Prop[];
}

/**
 * The record contributed by a single .property() call.
 *
 * A codec whose value type includes undefined describes an optional
 * property, not a required one holding undefined.
 */
type SingletonRecord<K extends keyof any, V> = undefined extends V
  ? { [Y in K]?: V }
  : { [Y in K]: V };

interface Prop {
  name: string;
  codec: Codec<any>;
  deprecated?: boolean;
}

interface Alternative {
  tagValue: any;
  codec: Codec<any>;
}

class ObjectCodecBuilder<OutputType, PartialOutputType> {
  private propList: Prop[] = [];
  private deprecatedProps: Set<string> = new Set();
  private _allowExtra: boolean = false;

  /**
   * Define a property for the object.
   */
  property<K extends keyof OutputType & string>(
    x: K,
    codec: Codec<OutputType[K]>,
  ): ObjectCodecBuilder<
    OutputType,
    PartialOutputType & SingletonRecord<K, OutputType[K]>
  > {
    if (!codec) {
      throw Error("inner codec must be defined");
    }
    this.propList.push({ name: x, codec: codec });
    return this as any;
  }

  /**
   * Define a property for the object.
   */
  propertyStrict<K extends keyof OutputType & string>(
    x: K,
    codec: Codec<OutputType[K]>,
  ): ObjectCodecBuilder<
    OutputType,
    PartialOutputType & SingletonRecord<K, OutputType[K]>
  > {
    if (!codec) {
      throw Error("inner codec must be defined");
    }
    this.propList.push({ name: x, codec: codec });
    return this as any;
  }

  /**
   * Concatenate properties from @a codec.
   *
   * @param other codec to concat properties from
   *
   * FIXME: do proper union of all `other' props.
   */
  mixin<K extends keyof OutputType & string, V extends OutputType[K]>(
    other: ObjectCodec<V>,
  ): ObjectCodecBuilder<OutputType, PartialOutputType & V> {
    this.propList.push(...other.getProps());
    return this as any;
  }

  /**
   * Define a deprecated property for the object.
   *
   * Deprecated properties won't be validated, their presence will
   * be validated in TRACE mode.
   */
  deprecatedProperty(
    x: string,
  ): ObjectCodecBuilder<OutputType, PartialOutputType> {
    this.deprecatedProps.add(x);
    return this as any;
  }

  /**
   * Do not log warnings if the object has extra properties.
   */
  allowExtra(): ObjectCodecBuilder<OutputType, PartialOutputType> {
    this._allowExtra = true;
    return this;
  }

  /**
   * Return the built codec.
   *
   * @param objectDisplayName name of the object that this codec operates on,
   *   used in error messages.
   */
  build(objectDisplayName: string): ObjectCodec<PartialOutputType> {
    const propList = this.propList;
    const allowExtra = this._allowExtra;
    const deprecatedPros = this.deprecatedProps;
    return {
      decode(x: any, c?: Context): PartialOutputType {
        if (!c) {
          c = {
            path: [`(${objectDisplayName})`],
          };
        }
        if (typeof x !== "object" || x === null || Array.isArray(x)) {
          throw new DecodingError(
            `expected object for ${objectDisplayName} at ${renderContext(
              c,
            )} but got ${typeof x}`,
          );
        }
        const obj: any = {};
        for (const prop of propList) {
          const propRawVal = x[prop.name];
          const propVal = prop.codec.decode(
            propRawVal,
            joinContext(c, prop.name),
          );
          obj[prop.name] = propVal;
        }
        for (const prop in x) {
          if (prop in obj) {
            continue;
          }
          if (allowExtra) {
            obj[prop] = x[prop];
          } else if (deprecatedPros.has(prop)) {
            logger.trace(
              `Deprecated property ${prop} for ${objectDisplayName} at ${renderContext(
                c,
              )}`,
            );
          } else {
            logger.warn(
              `Extra property ${prop} for ${objectDisplayName} at ${renderContext(
                c,
              )}`,
            );
          }
        }
        return obj as PartialOutputType;
      },

      getProps(): Prop[] {
        return propList;
      },
    };
  }
}

class UnionCodecBuilder<
  TargetType,
  TagPropertyLabel extends keyof TargetType,
  CommonBaseType,
  PartialTargetType,
> {
  private alternatives = new Map<any, Alternative>();

  constructor(
    private discriminator: TagPropertyLabel,
    private baseCodec?: Codec<CommonBaseType>,
  ) {}

  /**
   * Define a property for the object.
   */
  alternativeOnMissing<V>(
    tagValue: TargetType[TagPropertyLabel],
    codec: Codec<V>,
  ): UnionCodecBuilder<
    TargetType,
    TagPropertyLabel,
    CommonBaseType,
    PartialTargetType | V
  > {
    if (!codec) {
      throw Error("inner codec must be defined");
    }
    this.alternatives.set(undefined, { codec, tagValue });
    return this as any;
  }

  /**
   * Define a property for the object.
   */
  alternative<V>(
    tagValue: TargetType[TagPropertyLabel],
    codec: Codec<V>,
  ): UnionCodecBuilder<
    TargetType,
    TagPropertyLabel,
    CommonBaseType,
    PartialTargetType | V
  > {
    if (!codec) {
      throw Error("inner codec must be defined");
    }
    this.alternatives.set(tagValue, { codec, tagValue });
    return this as any;
  }

  /**
   * Return the built codec.
   *
   * @param objectDisplayName name of the object that this codec operates on,
   *   used in error messages.
   */
  build<R extends PartialTargetType & CommonBaseType = never>(
    objectDisplayName: string,
  ): Codec<R> {
    const alternatives = this.alternatives;
    const discriminator = this.discriminator;
    const baseCodec = this.baseCodec;
    return {
      decode(x: any, c?: Context): R {
        if (!c) {
          c = {
            path: [`(${objectDisplayName})`],
          };
        }
        if (typeof x !== "object" || x === null || Array.isArray(x)) {
          throw new DecodingError(
            `expected object for ${objectDisplayName} at ${renderContext(c)}`,
          );
        }
        const d = x[discriminator];
        if (d === undefined && !alternatives.has(d)) {
          throw new DecodingError(
            `expected tag for ${objectDisplayName} at ${renderContext(
              c,
            )}.${String(discriminator)}`,
          );
        }
        const alt = alternatives.get(d);
        if (!alt) {
          throw new DecodingError(
            `unknown tag for ${objectDisplayName} ${d} at ${renderContext(
              c,
            )}.${String(discriminator)}`,
          );
        }
        const altDecoded = alt.codec.decode(x, c);
        if (baseCodec) {
          const baseDecoded = baseCodec.decode(x, c);
          return { ...baseDecoded, ...altDecoded };
        } else {
          return altDecoded;
        }
      },
    };
  }
}

export class UnionCodecPreBuilder<T> {
  discriminateOn<D extends keyof T, B = {}>(
    discriminator: D,
    baseCodec?: Codec<B>,
  ): UnionCodecBuilder<T, D, B, never> {
    return new UnionCodecBuilder<T, D, B, never>(discriminator, baseCodec);
  }
}

/**
 * Return a builder for a codec that decodes an object with properties.
 */
export function buildCodecForObject<T>(): ObjectCodecBuilder<T, {}> {
  return new ObjectCodecBuilder<T, {}>();
}

export function buildCodecForUnion<T>(): UnionCodecPreBuilder<T> {
  return new UnionCodecPreBuilder<T>();
}

/**
 * Return a codec for a mapping from a string to values described by the inner codec.
 */
export function codecForMap<T>(
  innerCodec: Codec<T>,
): Codec<{ [x: string]: T }> {
  if (!innerCodec) {
    throw Error("inner codec must be defined");
  }
  return {
    decode(x: any, c?: Context): { [x: string]: T } {
      if (typeof x !== "object" || x === null || Array.isArray(x)) {
        throw new DecodingError(`expected object at ${renderContext(c)}`);
      }
      const map: { [x: string]: T } = {};
      for (const i of Object.keys(x)) {
        // Define rather than assign: assigning "__proto__" invokes the
        // Object.prototype setter instead of creating an own property.
        Object.defineProperty(map, i, {
          value: innerCodec.decode(x[i], joinContext(c, `[${i}]`)),
          writable: true,
          enumerable: true,
          configurable: true,
        });
      }
      return map;
    },
  };
}

/**
 * Return a codec for a list, containing values described by the inner codec.
 */
export function codecForList<T>(innerCodec: Codec<T>): Codec<T[]> {
  if (!innerCodec) {
    throw Error("inner codec must be defined");
  }
  return {
    decode(x: any, c?: Context): T[] {
      const arr: T[] = [];
      if (!Array.isArray(x)) {
        throw new DecodingError(`expected array at ${renderContext(c)}`);
      }
      for (const i in x) {
        arr.push(innerCodec.decode(x[i], joinContext(c, `[${i}]`)));
      }
      return arr;
    },
  };
}

/**
 * Return a codec for a value that must be a number.
 */
export function codecForNumber(): Codec<number> {
  return {
    decode(x: any, c?: Context): number {
      if (typeof x === "number") {
        return x;
      }
      throw new DecodingError(
        `expected number at ${renderContext(c)} but got ${typeof x}`,
      );
    },
  };
}

/**
 * Return a codec for a value that must be a number.
 */
export function codecForBoolean(): Codec<boolean> {
  return {
    decode(x: any, c?: Context): boolean {
      if (typeof x === "boolean") {
        return x;
      }
      throw new DecodingError(
        `expected boolean at ${renderContext(c)} but got ${typeof x}`,
      );
    },
  };
}

/**
 * Return a codec for a value that must be a string.
 */
export function codecForString(): Codec<string> {
  return {
    decode(x: any, c?: Context): string {
      if (typeof x === "string") {
        return x;
      }
      throw new DecodingError(
        `expected string at ${renderContext(c)} but got ${typeof x}`,
      );
    },
  };
}

export function codecForStringUnion<T extends Array<string>>(
  ...vals: [...T]
): Codec<T[number]> {
  return {
    decode(x: any, c?: Context): T[number] {
      if (typeof x !== "string") {
        throw new DecodingError(
          `expected string at ${renderContext(c)} but got ${typeof x}`,
        );
      }
      if (!vals.includes(x)) {
        throw new DecodingError(
          `expected constant of ${JSON.stringify(vals)} at ${renderContext(
            c,
          )} but got ${x}`,
        );
      }
      return x;
    },
  };
}

/**
 * Return a codec for a value that must be one of the values of a
 * string enum.
 *
 * Only usable with string enums: a numeric enum object also contains
 * the reverse mapping, which would be accepted as a valid value.
 */
export function codecForStringEnum<T extends string>(
  enumObj: Readonly<Record<string, T>>,
): Codec<T> {
  const vals = Object.values(enumObj);
  return {
    decode(x: any, c?: Context): T {
      if (typeof x !== "string") {
        throw new DecodingError(
          `expected string at ${renderContext(c)} but got ${typeof x}`,
        );
      }
      if (!vals.includes(x as T)) {
        throw new DecodingError(
          `expected one of ${JSON.stringify(vals)} at ${renderContext(
            c,
          )} but got ${x}`,
        );
      }
      return x as T;
    },
  };
}

/**
 * Return a codec for a string that must be an absolute http(s) URL.
 *
 * Parsing alone is not enough for the fields this guards: they are opened by
 * the browser or fetched over HTTP, and "javascript:", "data:" and "file:"
 * are all perfectly well-formed URLs.
 */
export function codecForHttpUrlString(
  shouldEndWithSlash?: boolean,
): Codec<string> {
  return {
    decode(x: any, c?: Context): string {
      if (typeof x !== "string") {
        throw new DecodingError(
          `expected string at ${renderContext(c)} but got ${typeof x}`,
        );
      }
      if (shouldEndWithSlash && !x.endsWith("/")) {
        throw new DecodingError(
          `expected URL string that ends with slash at ${renderContext(
            c,
          )} but got ${x}`,
        );
      }
      let url: URL;
      try {
        url = new URL(x);
      } catch {
        throw new DecodingError(
          `expected an absolute URL at ${renderContext(c)} but got "${x}"`,
        );
      }
      if (url.protocol !== "http:" && url.protocol !== "https:") {
        throw new DecodingError(
          `expected an http(s) URL at ${renderContext(c)} but got "${x}"`,
        );
      }
      return x;
    },
  };
}

/**
 * Check whether a URL is safe to hand to a browser as an external link.
 *
 * Plain HTTP is deliberately limited to loopback hosts so local development
 * remains possible without allowing an attacker to downgrade navigation to an
 * unencrypted remote origin.  User information is rejected because it can
 * obscure the actual destination in browser UI.
 */
export function isSafeExternalUrl(x: string | undefined): x is string {
  if (!x) return false;
  let url: URL;
  try {
    url = new URL(x);
  } catch {
    return false;
  }
  if (url.username !== "" || url.password !== "" || url.hostname === "") {
    return false;
  }
  if (url.protocol === "https:") return true;
  if (url.protocol !== "http:") return false;
  if (url.hostname === "localhost" || url.hostname === "[::1]") return true;
  const octets = url.hostname.split(".");
  return octets.length === 4 && octets[0] === "127";
}

/**
 * Return a codec for URLs that satisfy the external-navigation policy.
 */
export function codecForSafeExternalUrlString(): Codec<string> {
  return {
    decode(x: any, c?: Context): string {
      if (typeof x !== "string" || !isSafeExternalUrl(x)) {
        throw new DecodingError(
          `expected a safe external URL at ${renderContext(c)} but got ${JSON.stringify(x)}`,
        );
      }
      return x;
    },
  };
}

/**
 * Return a codec for a string that must be an absolute http(s) URL in
 * canonical base URL form.
 *
 * A base URL is only ever used by resolving a relative path against it, and
 * that quietly changes meaning when the URL isn't canonical: resolving "keys"
 * against "https://exchange.example.com/taler" yields
 * "https://exchange.example.com/keys", so we would talk to a different service
 * than the one we were given.  Rejecting is the only safe answer -- silently
 * canonicalizing would hide the misconfiguration from whoever can fix it.
 */
export function codecForCanonicalBaseUrlString(): Codec<string> {
  const urlCodec = codecForHttpUrlString();
  return {
    decode(x: any, c?: Context): string {
      const url = urlCodec.decode(x, c);
      if (canonicalizeBaseUrl(url) !== url) {
        throw new DecodingError(
          `expected a canonical base URL at ${renderContext(
            c,
          )} but got "${url}"`,
        );
      }
      return url;
    },
  };
}

/**
 * Return a codec for a value that must be a string.
 */
export function codecForStringURL(shouldEndWithSlash?: boolean): Codec<string> {
  return {
    decode(x: any, c?: Context): string {
      if (typeof x !== "string") {
        throw new DecodingError(
          `expected string at ${renderContext(c)} but got ${typeof x}`,
        );
      }
      if (shouldEndWithSlash && !x.endsWith("/")) {
        throw new DecodingError(
          `expected URL string that ends with slash at ${renderContext(
            c,
          )} but got ${x}`,
        );
      }
      try {
        new URL(x);
        return x;
      } catch (e) {
        if (e instanceof Error) {
          throw new DecodingError(e.message);
        } else {
          throw new DecodingError(
            `expected an URL string at ${renderContext(c)} but got "${x}"`,
          );
        }
      }
    },
  };
}

/**
 * Codec that allows any value.
 */
export function codecForAny(): Codec<any> {
  return {
    decode(x: any, c?: Context): any {
      return x;
    },
  };
}

/**
 * Return a codec for a value that must be a string.
 */
export function codecForConstString<V extends string>(s: V): Codec<V> {
  return {
    decode(x: any, c?: Context): V {
      if (x === s) {
        return x;
      }
      if (typeof x !== "string") {
        throw new DecodingError(
          `expected string constant "${s}" at ${renderContext(
            c,
          )} but got ${typeof x}`,
        );
      }
      throw new DecodingError(
        `expected string constant "${s}" at ${renderContext(
          c,
        )} but got string value "${x}"`,
      );
    },
  };
}

/**
 * Return a codec for a value that must be a constant number.
 */
export function codecForConstNumber<V extends number>(n: V): Codec<V> {
  return {
    decode(x: any, c?: Context): V {
      if (x === n) {
        return x;
      }
      throw new DecodingError(
        `expected number constant "${n}" at ${renderContext(
          c,
        )}  but got ${typeof x}`,
      );
    },
  };
}

export function codecOptional<V>(innerCodec: Codec<V>): Codec<V | undefined> {
  return {
    decode(x: any, c?: Context): V | undefined {
      if (x === undefined || x === null) {
        return undefined;
      }
      return innerCodec.decode(x, c);
    },
  };
}

export function codecOptionalDefault<V>(
  innerCodec: Codec<V>,
  def: V,
): Codec<V> {
  return {
    decode(x: any, c?: Context): V {
      if (x === undefined || x === null) {
        return def;
      }
      return innerCodec.decode(x, c);
    },
  };
}

export type CodecType<T> = T extends Codec<infer X> ? X : any;

// Codec<any>, not Codec<unknown>: Codec is invariant, and only `any` stays
// compatible in both directions.
export function codecForEither<T extends Array<Codec<any>>>(
  ...alts: [...T]
): Codec<CodecType<T[number]>> {
  return {
    decode(x: any, c?: Context): any {
      for (const alt of alts) {
        try {
          return alt.decode(x, c);
        } catch {
          continue;
        }
      }
      if (logger.shouldLogTrace()) {
        logger.trace(`offending value: ${j2s(x)}`);
      }
      throw new DecodingError(`No alternative matched at ${renderContext(c)}`);
    },
  };
}
